import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { get, map, findIndex, isEmpty, reduce, forEach } from 'lodash'

import VNBlueSwitch from '../VNBlueSwitch'
import SidebarModal from '../SidebarModal'
import BackChevron from '../BackChevron'
import Button from '../Button'

import { ReactComponent as Unchecked } from '../../assets/icons/fill-unchecked.svg'
import { ReactComponent as Checkmark } from '../../assets/icons/fill-checkmark.svg'

import { getOrderInProgress } from '../../selectors/order'
import { isTabbed, STADIUM_ORDER_STATES, wasTabbed } from '../../utils/orderStates'
import { ToastManager } from '../../utils/toastManager'

import styles from './RequestRefundModal.module.scss'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import { setManagerApprovalPrompt } from '../../VNManagerApproval/ActionCreators'
import { getTabsModeEnabled } from '../../selectors/menus'
import { makeGetRefund } from '../../selectors/refund'
import { requestRefund, requestTabbedRefund, requestTabbedRefundOffline } from '../../actions/refund'
import { useRefundHandler } from '../../VNRefunds/hooks/useRefundHandler'
import { useRefundCalculator } from '../../VNRefunds/hooks/useRefundCalculator'
import { setConfirmationDialog, setConnectionRequiredDialog } from '../../VNDialogs/ActionCreators'
import { APPLICATION_STRINGS } from '../../strings'
import VNRefundReasons from '../../VNRefunds/components/VNRefundReasons'

const centsToDollars = (cents) => `$${(cents / 100).toFixed(2)}`

const RequestRefundModal = ({ order, edit, onClose, className, ...props }) => {
  const dispatch = useDispatch()
  const isMountedRef = useRef(false)
  const getSelectedRefund = makeGetRefund()
  const refund = useSelector(getSelectedRefund)
  const classNames = cn(styles.requestRefundModal, className)
  const baseItems = get(order, 'itemModels', [])

  const items = useMemo(() => {
    const charityItemExists = baseItems[baseItems.length - 1]?.type === 'DonationLineItem'
    const donationLineItem = {
      name: 'Donation',
      priceInCents: order.charityAmountInCents,
      defaultPriceInCents: order.charityAmountInCents,
      modifiers: [],
      price: centsToDollars(order.charityAmountInCents),
      type: 'DonationLineItem',
      quantity: 1,
      modifiersString: '',
    }

    if (!order?.wasCreatedInStadium && order?.charityAmountInCents > 0 && !charityItemExists) {
      return [...baseItems, donationLineItem]
    }
    return baseItems
  }, [baseItems, order?.wasCreatedInStadium, order?.charityAmountInCents])

  const totalInCents = order?.amountInCents ?? 0
  const refundableItemIdIndexes = items.map((_, index) => index).filter((index) => !items[index].refund)
  const [selectedItems, setSelectedItems] = useState([])
  const [selectedModifiers, setSelectedModifiers] = useState({})
  const [refundAmount, setRefundAmount] = useState(0)
  const [isRefundCashRounded, setisRefundCashRounded] = useState(false)
  const [refundReason, setRefundReason] = useState(refund?.reason)
  const editMode = useMemo(() => edit, [edit])
  const isEntireOrder =
    (refundableItemIdIndexes.length > 0 && refundableItemIdIndexes.length === selectedItems.length) ||
    refundableItemIdIndexes.length === 0

  const isTabsMenu = useSelector(getTabsModeEnabled)
  const isAuthFailedTab = useMemo(() => {
    if (!isTabsMenu) return false

    // recovering from auth fail, if we void the trigger item, order enters "partially refunded" state
    if (order.state === STADIUM_ORDER_STATES.PARTIALLY_REFUNDED && order.balanceDueInCents > 0) return true

    return order.state === STADIUM_ORDER_STATES.AUTHORIZATION_FAILED
  }, [isTabsMenu, order])

  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const wasTab = wasTabbed(order) || isAuthFailedTab
  const isOrderTabbed = isTabbed(orderInProgress)
  const dollarAmount = centsToDollars(refundAmount)
  const { hasCardPresentRefund, managerSuccessCallback } = useRefundHandler()
  const { calculateItemsRefund } = useRefundCalculator()
  const isCardPresentRefund = useMemo(() => !!hasCardPresentRefund(), [hasCardPresentRefund])

  /**
   * refundTabbedOrder logic:
   *
   *          order in Stadium   | order not in Stadium
   *         +-------------------+---------------------+
   * online  | call mutation api | void items locally  |
   *         +-------------------+---------------------+
   * offline | can't void items  | void items locally  |
   *         +-------------------+---------------------+
   *
   */
  const refundTabbedOrder = useCallback(
    ({ orderId, itemIndexes, itemIds, lineItemIds, reason: refundReason, amountInCents: refundAmount, cardReaderData }) => {
      const networkConnected = getNetworkAvailableValue()
      const payload = {
        orderId,
        itemIndexes,
        itemIds,
        lineItemIds,
        reason: refundReason,
        amountInCents: refundAmount,
        isCardPresentRefund,
        cardReaderData,
        isCharitySelected: itemIndexes.some((itemIndex) => itemIndex === items?.length - 1),
      }

      //Online processing
      if (networkConnected) {
        //Order aready in Stadium then we can mutate the order
        if (order.wasCreatedInStadium) {
          dispatch(requestTabbedRefund(payload))
          return
        }
        //Order is not in Stadium so even we're online we can't mutate we can just do a local update
        dispatch(requestTabbedRefundOffline(payload))
        return
      }

      //offline and order already in stadium then we need to error off
      if (order.wasCreatedInStadium) {
        ToastManager.error("Items can't be voided offline for this order")
        return
      }

      //offline and order is not in stadium
      dispatch(requestTabbedRefundOffline(payload))
    },
    [dispatch, isCardPresentRefund, order.wasCreatedInStadium, items?.length],
  )

  const handleOnComplete = useCallback(
    (cardReaderData = null) => {
      if (!isMountedRef.current) {
        // delayed cleanup state for CFD
        dispatch(setManagerApprovalPrompt({ onSuccess: () => null, isOpen: false }))
        managerSuccessCallback(null, null)
        return
      }

      const orderId = order.uuid
      const itemIndexes = selectedItems
      const itemIds = map(selectedItems, (itemIndex) => items[itemIndex].id)

      const lineItemIds = reduce(
        selectedItems,
        (allIds, itemIndex) => {
          return [...allIds, items[itemIndex]?.lineItemUuid]
        },
        Object.keys(selectedModifiers),
      )

      forEach(selectedModifiers, (modifier) => (modifier.refund = true))

      if (isOrderTabbed || wasTab) {
        // need to pass cardReaderData
        refundTabbedOrder({
          orderId,
          itemIndexes,
          itemIds,
          lineItemIds,
          reason: refundReason,
          amountInCents: refundAmount,
          cardReaderData,
        })
        return
      }

      if (!getNetworkAvailableValue() && order?.wasCreatedInStadium) {
        dispatch(setConnectionRequiredDialog({ open: true }))
        return
      }

      dispatch(
        requestRefund({
          orderId,
          itemIndexes,
          itemIds,
          lineItemIds,
          reason: refundReason,
          amountInCents: refundAmount,
          isCardPresentRefund,
          cardReaderData,
          isCharitySelected: itemIndexes.some((itemIndex) => itemIndex === items?.length - 1),
        }),
      )
    },
    [
      order.uuid,
      order?.wasCreatedInStadium,
      selectedItems,
      selectedModifiers,
      isOrderTabbed,
      wasTab,
      dispatch,
      refundReason,
      refundAmount,
      isCardPresentRefund,
      managerSuccessCallback,
      items,
      refundTabbedOrder,
    ],
  )

  const handleComplete = useCallback(
    (isSuccess = true, cardData = null) => {
      if (!isSuccess) {
        onClose()
        return
      }

      handleOnComplete(cardData)
      onClose()
    },
    [handleOnComplete, onClose],
  )

  // used when refunding with card present to avoid setting state on unmounted component
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const { calculatedRefundInCents, isRefundCashRounded } = calculateItemsRefund(selectedItems, selectedModifiers, isEntireOrder)
    setRefundAmount(calculatedRefundInCents)
    setisRefundCashRounded(isRefundCashRounded)
  }, [calculateItemsRefund, selectedItems, selectedModifiers, isEntireOrder])

  useEffect(() => {
    if (refund && refund.reason) {
      const reason = refund.reason
      setRefundReason(reason)
    }
  }, [items, refund])

  const toggleItem = (index, item) => {
    //once refunded... don't let the user change the toggle
    if (item.refund) return
    const isChecked = selectedItems.includes(index)
    if (isChecked) {
      selectedItems.splice(
        findIndex(selectedItems, (item) => item === index),
        1,
      )
      setSelectedItems([...selectedItems])
      const currentModifiers = {}
      for (const modIndex in selectedModifiers) {
        if (selectedModifiers[modIndex].parentItemIndex === index) {
          continue
        }
        currentModifiers[modIndex] = selectedModifiers[modIndex]
      }
      setSelectedModifiers(currentModifiers)
    } else {
      setSelectedItems([index, ...selectedItems])
      forEach(item.modifiers, (modifier) => {
        if (!modifier.refund) {
          selectedModifiers[modifier.lineItemUuid] = {
            ...modifier,
            parentItemIndex: index,
          }
        }
      })
      setSelectedModifiers({ ...selectedModifiers })
    }
  }

  const toggleModifier = (modifier, itemIndex) => {
    //once refunded... don't let the user change the toggle
    if (modifier.refund) return
    const isChecked = selectedModifiers[modifier.lineItemUuid]
    if (isChecked) {
      delete selectedModifiers[modifier.lineItemUuid]
      setSelectedModifiers({ ...selectedModifiers })
    } else {
      selectedModifiers[modifier.lineItemUuid] = {
        ...modifier,
        parentItemIndex: itemIndex,
      }
      setSelectedModifiers({ ...selectedModifiers })
    }
  }

  const onSelectEntireOrder = () => {
    if (!editMode) return
    if (isEntireOrder) {
      setSelectedItems([])
      setSelectedModifiers({})
      return
    }

    const newSelectedModifiers = reduce(
      items,
      (modifiers, item) => {
        return {
          ...modifiers,
          ...reduce(
            item.modifiers,
            (itemModifiers, modifier) => {
              return !modifier?.refund ? { ...itemModifiers, [modifier?.lineItemUuid]: modifier } : itemModifiers
            },
            {},
          ),
        }
      },
      {},
    )
    setSelectedItems([...refundableItemIdIndexes])
    setSelectedModifiers(newSelectedModifiers)
  }

  const getCheckboxStyle = (refund, isItemChecked, isModifierChecked) => {
    return isModifierChecked || (isItemChecked && !isModifierChecked) ? (
      <Checkmark
        className={(isItemChecked && isModifierChecked) || refund ? styles.checkBoxIconUnSelectable : styles.checkBoxIcon}
      />
    ) : (
      <Unchecked className={styles.checkBoxIcon} />
    )
  }

  const renderItems = (item, index) => {
    const isItemChecked = selectedItems.includes(index) || item.refund

    return (
      <div key={index} className={styles.selectableItemContainer}>
        <div className={styles.selectableItem} key={`${item.name}-${index}`} onClick={() => toggleItem(index, item)}>
          <div className={cn([styles.item, isItemChecked && styles.selected])}>
            <div className={styles.column}>{getCheckboxStyle(item.refund, isItemChecked)}</div>
            <div className={styles.column}>
              <span className={styles.name}>{`(1) ${item.name} - ${centsToDollars(item.priceInCents)}`}</span>
            </div>
          </div>
        </div>

        {map(item.modifiers, (modifier, modIndex) => {
          const isModifierChecked = selectedModifiers[modifier.lineItemUuid] || modifier.refund

          return (
            <div
              className={styles.selectableItem}
              key={`${modifier?.name}-${index}-${modIndex}`}
              onClick={() => {
                if (isItemChecked) return
                toggleModifier(modifier, index)
              }}
            >
              <div className={cn([styles.item, isModifierChecked && styles.selected])}>
                <div className={styles.modColumn}>{getCheckboxStyle(modifier.refund, isItemChecked, isModifierChecked)}</div>
                <div className={styles.column}>
                  <span
                    className={styles.name}
                  >{`(1) ${modifier.name} - $${(modifier.defaultPriceInCents / 100).toFixed(2)}`}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const onVoidHandler = () => {
    const onSuccess = () => managerSuccessCallback(dollarAmount, handleComplete, isMountedRef)
    const buttonText = `Void ${dollarAmount}`

    if (isCardPresentRefund) {
      dispatch(
        setConfirmationDialog(APPLICATION_STRINGS.REFUND_INTERAC_CONFIRMATION, null, () => {
          dispatch(setManagerApprovalPrompt({ onSuccess, buttonText }))
        }),
      )
      return
    }
    dispatch(setManagerApprovalPrompt({ onSuccess, buttonText }))
  }

  return (
    <SidebarModal
      show
      title="Void Items"
      onClose={onClose}
      leftAccessory={<BackChevron onClick={onClose} />}
      className={classNames}
      {...props}
    >
      <div className={styles.requestRefundModal}>
        <div className={styles.orderNumber}>{`Order # ${order.orderNumber}`}</div>
        <VNRefundReasons reason={refund?.reason} editMode={editMode} onChange={setRefundReason} />
        <div className={styles.requiredField}>* Required Field</div>
        <div className={styles.refundContainer}>
          <span className={styles.border} />
          <div className={styles.selectItems}>Select items</div>
          <div className={styles.selectableItem} key={'Entire Order'} onClick={onSelectEntireOrder}>
            <div className={cn([styles.item, styles.entireOrder])}>
              <div className={styles.column}>
                <span className={styles.name}>{`Entire Order - ${centsToDollars(totalInCents)}`}</span>
              </div>
              <div className={cn([styles.colomn, styles.entireOrderToggle])}>
                <VNBlueSwitch disableRipple checked={isEntireOrder} />
              </div>
            </div>
          </div>
          <div className={styles.cashRoundedVerbiage}>
            {isRefundCashRounded && 'Amount has been rounded to the nearest nickel'}
          </div>
          {map(items, renderItems)}
        </div>
        <div className={styles.gradient}></div>
        <div className={styles.submitContainer}>
          {editMode && (
            <Button
              useMui
              className={styles.submitButton}
              disabled={(isEmpty(selectedItems) && isEmpty(selectedModifiers)) || refundReason === undefined}
              onClick={onVoidHandler}
            >
              Void {dollarAmount}
            </Button>
          )}
        </div>
      </div>
    </SidebarModal>
  )
}

RequestRefundModal.defaultProps = {
  order: {},
  onClose: () => {},
  edit: true,
}

RequestRefundModal.propTypes = {
  order: PropTypes.object,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
}

export default RequestRefundModal
